/**
 * Collection Compare (E20-1086 roadmap)
 *
 * A two-column comparison where BOTH columns are shopper-selectable (unlike the PDP
 * Product Compare, where the left column is locked). Each selectable product is
 * pre-rendered into a <template>; changing a column's <select> clones the matching
 * template into that column's card. <compare-heights> re-aligns the rows automatically
 * via its own MutationObserver, so no manual re-measure is needed.
 *
 * The two columns are kept mutually exclusive: in each dropdown the product currently
 * shown in the OTHER column is disabled, so a product can never be compared with itself
 * (no error state needed — every product still appears in both dropdowns).
 *
 * The guard wraps the class too, not just the define(): the section emits this tag per
 * instance, and a page with two of them would otherwise re-declare a top-level binding
 * and throw at parse time, before any guard could run.
 */

if (!customElements.get('collection-compare')) {
  class CollectionCompare extends HTMLElement {
    connectedCallback() {
      this.sectionId = this.dataset.sectionId;
      this.selects = [...this.querySelectorAll('[data-compare-select]')];
      if (!this.sectionId || this.selects.length < 2) return;

      this.controller = new AbortController();
      this.selects.forEach((select) =>
        select.addEventListener('change', () => this.onChange(select), {
          signal: this.controller.signal,
        })
      );

      this.syncOptions();
    }

    disconnectedCallback() {
      this.controller?.abort();
    }

    onChange(select) {
      const { slot } = select.dataset;
      const template = this.querySelector(
        `#collection-compare-tpl-${CSS.escape(this.sectionId)}-${CSS.escape(select.value)}`
      );
      const card = this.querySelector(`[data-compare-card][data-slot="${slot}"]`);

      if (template && card) {
        card.replaceChildren(template.content.cloneNode(true));
        // Let integrations react to the new product (e.g. re-scan Bazaarvoice ratings).
        this.dispatchEvent(
          new CustomEvent('collection-compare:swapped', {
            bubbles: true,
            detail: { handle: select.value, slot },
          })
        );
      }

      this.syncOptions();
    }

    // Keep the columns mutually exclusive: disable, in each dropdown, the product shown
    // in the other column so it can't be selected on both sides at once.
    syncOptions() {
      const [first, second] = this.selects;
      this.disableValue(first, second.value);
      this.disableValue(second, first.value);
    }

    disableValue(select, value) {
      [...select.options].forEach((option) => {
        option.disabled = option.value === value;
      });
    }
  }

  customElements.define('collection-compare', CollectionCompare);
}
